import { useState, useEffect } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import './Admin.css';

interface TokenResult {
  tokenId: string;
  uploadUrl: string;
  folderInfo?: {
    folderId: string;
    folderName: string;
    folderLink: string;
  };
}

interface TokenAnalytics {
  tokenId: string;
  clientLabel: string;
  bytesUploaded: number;
  filesUploaded: number;
  createdAt: string;
  expiresAt: string;
  revoked: boolean;
  status: 'active' | 'expired' | 'revoked';
}

interface AnalyticsData {
  tokens: TokenAnalytics[];
  totals: {
    totalTokens: number;
    totalUploads: number;
    totalStorageBytes: number;
    activeTokens: number;
    expiredTokens: number;
    revokedTokens: number;
  };
}

export default function Admin() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [clientLabel, setClientLabel] = useState('');
  const [driveFolderId, setDriveFolderId] = useState('');
  const [maxTotalBytes, setMaxTotalBytes] = useState(20); // GB
  const [generating, setGenerating] = useState(false);
  const [generatedToken, setGeneratedToken] = useState<TokenResult | null>(null);
  const [error, setError] = useState('');
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);

  useEffect(() => {
    // Check if already authenticated (session stored in localStorage)
    const sessionToken = localStorage.getItem('adminSessionToken');
    if (sessionToken) {
      setIsAuthenticated(true);
      fetchAnalytics(sessionToken);
    }
    setIsLoading(false);
  }, []);

  const fetchAnalytics = async (sessionToken: string) => {
    setLoadingAnalytics(true);
    try {
      const response = await fetch(
        `/.netlify/functions/adminAnalytics?token=${encodeURIComponent(sessionToken)}`
      );

      if (response.status === 401) {
        // Session expired
        handleLogout();
        return;
      }

      const data = await response.json();

      if (data.success) {
        setAnalytics(data);
      } else {
        console.error('Failed to fetch analytics:', data.error);
      }
    } catch (err) {
      console.error('Error fetching analytics:', err);
    } finally {
      setLoadingAnalytics(false);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError('');

    try {
      const response = await fetch('/.netlify/functions/adminAuth', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });

      const data = await response.json();

      if (data.success && data.token) {
        localStorage.setItem('adminSessionToken', data.token);
        setIsAuthenticated(true);
        fetchAnalytics(data.token);
      } else {
        setLoginError(data.error || 'Invalid password');
      }
    } catch (err) {
      setLoginError('Failed to connect to server');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminSessionToken');
    setIsAuthenticated(false);
    setGeneratedToken(null);
    setAnalytics(null);
  };

  const handleGenerateToken = async (e: React.FormEvent) => {
    e.preventDefault();
    setGenerating(true);
    setError('');
    setGeneratedToken(null);

    try {
      const sessionToken = localStorage.getItem('adminSessionToken');
      if (!sessionToken) {
        setError('Not authenticated');
        return;
      }

      const response = await fetch('/.netlify/functions/adminGenerateToken', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          clientLabel: clientLabel,
          driveFolderId: driveFolderId || undefined,
          maxTotalBytes: maxTotalBytes * 1024 * 1024 * 1024, // Convert GB to bytes
          adminToken: sessionToken,
        }),
      });

      const data = await response.json();

      if (data.success) {
        setGeneratedToken(data);
        setClientLabel(''); // Reset form
        setDriveFolderId('');
        // Refresh analytics
        fetchAnalytics(sessionToken);
      } else {
        setError(data.error || 'Failed to generate token');
        if (data.error?.includes('Unauthorized')) {
          handleLogout();
        }
      }
    } catch (err) {
      setError('Failed to generate token');
    } finally {
      setGenerating(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    alert('Copied to clipboard!');
  };

  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString: string): string => {
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  if (isLoading) {
    return (
      <>
        <Header />
        <div className="admin-container">
          <div className="admin-loading">Loading...</div>
        </div>
        <Footer />
      </>
    );
  }

  if (!isAuthenticated) {
    return (
      <>
        <Header />
        <div className="admin-container">
          <div className="admin-login">
            <h1>Admin Login</h1>
            <form onSubmit={handleLogin}>
              <div className="form-group">
                <label htmlFor="password">Password</label>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
              {loginError && <div className="error-message">{loginError}</div>}
              <button type="submit" className="btn-primary">
                Login
              </button>
            </form>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="admin-container">
        <div className="admin-dashboard">
          <div className="admin-header">
            <h1>Admin Dashboard</h1>
            <button onClick={handleLogout} className="btn-secondary">
              Logout
            </button>
          </div>

          <div className="admin-content">
            <section className="token-generator">
              <h2>Generate Client Upload URL</h2>
              <form onSubmit={handleGenerateToken}>
                <div className="form-group">
                  <label htmlFor="clientLabel">Client Name *</label>
                  <input
                    type="text"
                    id="clientLabel"
                    value={clientLabel}
                    onChange={(e) => setClientLabel(e.target.value)}
                    placeholder="e.g., John Smith"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="driveFolderId">
                    Google Drive Folder ID (optional)
                  </label>
                  <input
                    type="text"
                    id="driveFolderId"
                    value={driveFolderId}
                    onChange={(e) => setDriveFolderId(e.target.value)}
                    placeholder="Leave empty to create new folder"
                  />
                  <small>
                    If left empty, a new folder will be created automatically
                  </small>
                </div>

                <div className="form-group">
                  <label htmlFor="maxTotalBytes">Max Upload Size (GB)</label>
                  <input
                    type="number"
                    id="maxTotalBytes"
                    value={maxTotalBytes}
                    onChange={(e) => setMaxTotalBytes(Number(e.target.value))}
                    min="1"
                    max="100"
                  />
                </div>

                {error && <div className="error-message">{error}</div>}

                <button
                  type="submit"
                  className="btn-primary"
                  disabled={generating}
                >
                  {generating ? 'Generating...' : 'Generate Upload URL'}
                </button>
              </form>
            </section>

            {generatedToken && (
              <section className="token-result">
                <h2>Generated Upload URL</h2>
                <div className="result-card">
                  <div className="result-field">
                    <label>Upload URL:</label>
                    <div className="url-display">
                      <code>{generatedToken.uploadUrl}</code>
                      <button
                        onClick={() => copyToClipboard(generatedToken.uploadUrl)}
                        className="btn-copy"
                      >
                        Copy
                      </button>
                    </div>
                  </div>

                  <div className="result-field">
                    <label>Token ID:</label>
                    <div className="url-display">
                      <code>{generatedToken.tokenId}</code>
                      <button
                        onClick={() =>
                          copyToClipboard(generatedToken.tokenId)
                        }
                        className="btn-copy"
                      >
                        Copy
                      </button>
                    </div>
                  </div>

                  {generatedToken.folderInfo && (
                    <div className="result-field">
                      <label>Google Drive Folder:</label>
                      <div>
                        <a
                          href={generatedToken.folderInfo.folderLink}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {generatedToken.folderInfo.folderName}
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              </section>
            )}

            <section className="analytics-section">
              <div className="analytics-header">
                <h2>Analytics</h2>
                <button
                  onClick={() => {
                    const token = localStorage.getItem('adminSessionToken');
                    if (token) fetchAnalytics(token);
                  }}
                  className="btn-secondary btn-refresh"
                  disabled={loadingAnalytics}
                >
                  {loadingAnalytics ? 'Loading...' : 'Refresh'}
                </button>
              </div>

              {analytics && (
                <>
                  <div className="analytics-totals">
                    <div className="total-card">
                      <div className="total-label">Total Tokens</div>
                      <div className="total-value">{analytics.totals.totalTokens}</div>
                    </div>
                    <div className="total-card">
                      <div className="total-label">Total Uploads</div>
                      <div className="total-value">{analytics.totals.totalUploads}</div>
                    </div>
                    <div className="total-card">
                      <div className="total-label">Total Storage</div>
                      <div className="total-value">
                        {formatBytes(analytics.totals.totalStorageBytes)}
                      </div>
                    </div>
                    <div className="total-card">
                      <div className="total-label">Active</div>
                      <div className="total-value">{analytics.totals.activeTokens}</div>
                    </div>
                    <div className="total-card">
                      <div className="total-label">Expired</div>
                      <div className="total-value">{analytics.totals.expiredTokens}</div>
                    </div>
                    <div className="total-card">
                      <div className="total-label">Revoked</div>
                      <div className="total-value">{analytics.totals.revokedTokens}</div>
                    </div>
                  </div>

                  <div className="analytics-table-container">
                    <table className="analytics-table">
                      <thead>
                        <tr>
                          <th>Client</th>
                          <th>Files</th>
                          <th>Storage</th>
                          <th>Created</th>
                          <th>Expires</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {analytics.tokens.length === 0 ? (
                          <tr>
                            <td colSpan={6} className="no-data">
                              No tokens found
                            </td>
                          </tr>
                        ) : (
                          analytics.tokens.map((token) => (
                            <tr key={token.tokenId}>
                              <td>{token.clientLabel}</td>
                              <td>{token.filesUploaded}</td>
                              <td>{formatBytes(token.bytesUploaded)}</td>
                              <td>{formatDate(token.createdAt)}</td>
                              <td>{formatDate(token.expiresAt)}</td>
                              <td>
                                <span className={`status-badge status-${token.status}`}>
                                  {token.status}
                                </span>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              {!analytics && !loadingAnalytics && (
                <div className="no-analytics">No analytics data available</div>
              )}
            </section>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
}

